import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useWindowSize from '../../hooks/useWindowSize';
import language_icon from '../../assets/icons/language-icon.jpg';
import menu_icon from '../../assets/icons/menu-icon.jpg';
import close_icon from '../../assets/icons/close-icon.jpg';
import { PATHS } from '../../config/consts';

const navLinks = [
    { id: 1, name: 'home-nav-item', target: '#about' },
    { id: 2, name: 'resume-nav-item', target: '#resume' },
    { id: 3, name: 'services-nav-item', target: '#services' },
    { id: 4, name: 'projects-nav-item', target: '#projects' },
    { id: 5, name: 'skills-nav-item', target: '#skills' },
    { id: 6, name: 'contact-nav-item', target: '#contact' },
];

const Navbar = () => {
    const windowSize = useWindowSize();
    const isLargeScreen = windowSize > 767;

    const { i18n, t } = useTranslation();
    const isRtl = i18n.language === 'he';

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [selectedLink, setSelectedLink] = useState(navLinks[0]);
    const navbarRef = useRef(null);

    const handleClickOutside = (event) => {
        if (navbarRef.current && !navbarRef.current.contains(event.target)) {
            const navbarToggler = document.querySelector('.navbar-toggler');
            if (navbarToggler) navbarToggler.click();
        }
    };

    const handleNavbarScroll = useCallback(() => {
        const navbar = document.querySelector('.navbar');
        if (isLargeScreen && window.scrollY !== 0) {
            navbar?.classList.add('scrolled');
        } else {
            navbar?.classList.remove('scrolled');
        }
    }, [isLargeScreen]);

    const handleSectionHighlight = useCallback(() => {
        const fromTop = window.scrollY + window.innerHeight / 20;
        let currentSection = navLinks[0];

        for (const navLink of navLinks) {
            const section = document.querySelector(navLink.target);
            if (section) {
                const top = section.offsetTop;
                const height = section.offsetHeight;
                if (fromTop >= top && fromTop < top + height) {
                    currentSection = navLink;
                    break;
                }
            }
        }

        if (currentSection.id !== selectedLink.id) {
            setSelectedLink(currentSection);
        }

        if (window.innerHeight + window.scrollY >= document.body.offsetHeight) {
            setSelectedLink(navLinks[navLinks.length - 1]);
        }
    }, [selectedLink]);

    useEffect(() => {
        const handleScroll = () => {
            handleNavbarScroll();
            handleSectionHighlight();
        };
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isLargeScreen, selectedLink, handleNavbarScroll, handleSectionHighlight]);

    useEffect(() => {
        if (isSidebarOpen) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isSidebarOpen]);

    const insidePageNavigation = (target) => {
        const element = document.querySelector(target);
        const navbarToggler = document.querySelector('.navbar-toggler');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
        if (navbarToggler && !isLargeScreen) {
            navbarToggler.click();
        }
    };

    const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

    const changeLanguage = (e) => i18n.changeLanguage(e.target.value);

    const renderBrand = () => {
        return (
            <div
                className={`navbar-brand ${isRtl ? 'me-0 ms-3' : ''} d-none d-md-block`}
                onClick={() => insidePageNavigation('#root')}
            >
                {t('brand-name')}
            </div>
        );
    };

    const renderNavLinks = () =>
        navLinks.map((navLink) => {
            const isActive = selectedLink.id === navLink.id;
            return (
                <li className='nav-item' key={navLink.id}>
                    <a
                        className={`nav-link ${isActive ? 'active' : ''}`}
                        onClick={() => insidePageNavigation(navLink.target)}
                        href={navLink.href}
                        download={navLink.download}
                    >
                        {t(navLink.name)}
                    </a>
                </li>
            );
        });

    return (
        <nav ref={navbarRef} className={`navbar navbar-expand-md fixed-top ${isSidebarOpen ? 'sidebar' : ''}`}>
            <div className='container'>
                {renderBrand()}
                <div className={`${isRtl ? 'ms' : 'me'}-auto`}>
                    {!isLargeScreen && (
                        <button
                            className='navbar-toggler'
                            type='button'
                            aria-label='Toggle navigation'
                            onClick={toggleSidebar}
                        >
                            <div className={isSidebarOpen ? 'navbar-toggler-box-closed' : 'navbar-toggler-box'}>
                                <img
                                    className='navbar-toggler-icon'
                                    src={isSidebarOpen ? close_icon : menu_icon}
                                    alt='toggle'
                                />
                            </div>
                        </button>
                    )}
                </div>
                <div className={`collapse navbar-collapse${isSidebarOpen ? ' show' : ''}`} id='navbarResponsive'>
                    {' '}
                    <>
                        <ul className='navbar-nav text-uppercase py-4 py-md-0 px-0 mx-auto'>{renderNavLinks()}</ul>
                        <ul className='navbar-nav text-uppercase px-0'>
                            {!isLargeScreen && (
                                <li className='nav-item'>
                                    <a className='nav-link' href={PATHS.RESUME} download>
                                        {t('header-resume')}
                                    </a>
                                </li>
                            )}
                            <li className='nav-item dropdown'>
                                <div
                                    className='nav-link'
                                    id='navbarDropdown'
                                    role='button'
                                    data-bs-toggle='dropdown'
                                    aria-expanded='false'
                                >
                                    {isLargeScreen ? (
                                        <img className='language-icon' src={language_icon} alt='language icon' />
                                    ) : (
                                        <div>{t('language-nav-item')}</div>
                                    )}
                                </div>
                                <ul className='dropdown-menu' aria-labelledby='navbarDropdown'>
                                    <li>
                                        <button
                                            className={`dropdown-item ${isRtl ? 'text-end' : ''}`}
                                            onClick={changeLanguage}
                                            value='he'
                                        >
                                            עברית
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className={`dropdown-item ${isRtl ? 'text-end' : ''}`}
                                            onClick={changeLanguage}
                                            value='en'
                                        >
                                            English
                                        </button>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
